import { ArrowDown, ArrowUp, GripVertical, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { type DragEvent, type Ref } from 'react';

import { cx } from '@/lib/cx';
import { Badge } from '@/ui/primitives/badge';
import { Button } from '@/ui/primitives/button';
import { DropdownMenu } from '@/ui/primitives/dropdown-menu';
import { Interruptor } from '@/ui/primitives/interruptor';
import { type RegraClassificacao } from '@fiscoai/contracts';

import { FraseRegra } from './frase-regra';
import { itens } from './textos';

export interface AcoesLinha {
  aoAlternar: (ligar: boolean) => void;
  aoMover: (direcao: -1 | 1) => void;
  aoEditar: () => void;
  aoApagar: () => void;
}

export interface ArrasteLinha {
  aoComecar: () => void;
  aoPassar: () => void;
  aoSoltar: () => void;
  aoTerminar: () => void;
  /** Outra regra está sendo arrastada sobre esta. */
  alvo: boolean;
}

interface Props extends AcoesLinha {
  regra: RegraClassificacao;
  posicao: number;
  total: number;
  podeEditar: boolean;
  ocupada: boolean;
  arraste: ArrasteLinha;
  ref?: Ref<HTMLLIElement>;
}

function Contagem({ regra }: { regra: RegraClassificacao }) {
  if (!regra.ativa)
    return <p className="text-xs text-texto-fraco">Desligada: não classifica nenhum item.</p>;
  return (
    <p className="text-xs text-texto-fraco">
      {regra.itens === 0 ? 'Ainda não classificou nenhum item' : `Classifica ${itens(regra.itens)}`}
    </p>
  );
}

/** ↑ ↓ e o menu ⋯ (editar, apagar). As setas são o caminho do teclado para reordenar. */
function Acoes({ regra, posicao, total, ...p }: Omit<Props, 'arraste' | 'ref' | 'podeEditar'>) {
  return (
    <span className="flex shrink-0 gap-0.5">
      <Button
        variante="fantasma"
        tamanho="icone-sm"
        data-mover="subir"
        aria-label={`Subir ${regra.nome}`}
        disabled={posicao === 0}
        onClick={() => {
          p.aoMover(-1);
        }}
      >
        <ArrowUp aria-hidden />
      </Button>
      <Button
        variante="fantasma"
        tamanho="icone-sm"
        data-mover="descer"
        aria-label={`Descer ${regra.nome}`}
        disabled={posicao === total - 1}
        onClick={() => {
          p.aoMover(1);
        }}
      >
        <ArrowDown aria-hidden />
      </Button>
      <DropdownMenu
        gatilho={
          <Button variante="fantasma" tamanho="icone-sm" aria-label={`Mais ações: ${regra.nome}`}>
            <MoreHorizontal aria-hidden />
          </Button>
        }
        itens={[
          { rotulo: 'Editar', icone: <Pencil aria-hidden />, aoSelecionar: p.aoEditar },
          {
            rotulo: 'Apagar',
            icone: <Trash2 aria-hidden />,
            aoSelecionar: p.aoApagar,
            perigoso: true,
          },
        ]}
      />
    </span>
  );
}

/** Alça ⠿: arrastar é atalho do mouse; o teclado usa ↑/↓ (a alça fica fora da leitura). */
function Alca({ arraste, nome }: { arraste: ArrasteLinha; nome: string }) {
  return (
    <span
      draggable
      aria-hidden
      title={`Arrastar ${nome}`}
      className="cursor-grab text-texto-fraco hover:text-texto active:cursor-grabbing [&_svg]:size-4"
      onDragStart={(e: DragEvent<HTMLSpanElement>) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', nome);
        const linha = e.currentTarget.closest('li');
        if (linha) e.dataTransfer.setDragImage(linha, 16, 16);
        arraste.aoComecar();
      }}
      onDragEnd={arraste.aoTerminar}
    >
      <GripVertical />
    </span>
  );
}

/** Uma regra na lista: posição (é prioridade), liga/desliga, a frase e quantos itens pega. */
export function LinhaRegra({ regra, posicao, total, podeEditar, arraste, ref, ...acoes }: Props) {
  return (
    <li
      ref={ref}
      style={{ viewTransitionName: `regra-${regra.id}` }}
      className={cx(
        'grid grid-cols-[auto_1fr] gap-x-3 px-4 py-3 transition-colors duration-[var(--duracao-rapida)]',
        arraste.alvo && 'bg-superficie-selecao',
      )}
      onDragOver={(e) => {
        e.preventDefault();
        arraste.aoPassar();
      }}
      onDrop={(e) => {
        e.preventDefault();
        arraste.aoSoltar();
      }}
    >
      <div className="flex items-start gap-1 pt-px">
        <span className="w-6 text-right text-lg font-semibold leading-tight tabular text-marca">
          {posicao + 1}
        </span>
        {podeEditar && <Alca arraste={arraste} nome={regra.nome} />}
      </div>
      <div className="grid min-w-0 gap-1.5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          {podeEditar ? (
            <Interruptor
              rotulo={regra.nome}
              ligado={regra.ativa}
              estado={regra.ativa ? 'Ligada' : 'Desligada'}
              disabled={acoes.ocupada}
              aoMudar={acoes.aoAlternar}
            />
          ) : (
            <span className="flex items-center gap-2 font-medium">
              {regra.nome}
              {!regra.ativa && <Badge tom="neutro">Desligada</Badge>}
            </span>
          )}
          {podeEditar && <Acoes regra={regra} posicao={posicao} total={total} {...acoes} />}
        </div>
        <FraseRegra regra={regra} />
        <Contagem regra={regra} />
      </div>
    </li>
  );
}
